<template>
  <v-dialog :value="value" persistent scrollable max-width="960" @input="$emit('input', $event)">
    <v-card class="ds-dialog" color="bg1">
      <div class="ds-dialog__header">
        <div class="ds-dialog__header-icon">
          <v-icon color="warning" size="20">mdi-source-merge</v-icon>
        </div>
        <div class="ds-dialog__header-text">
          <div class="ds-dialog__title">This draft changed while you were editing</div>
          <div class="ds-dialog__subtitle">
            Someone else saved a newer version of these release notes. Compare the two below, then
            choose which one to keep.
          </div>
        </div>
      </div>

      <div class="ds-dialog__body">
        <v-row dense>
          <v-col cols="12" md="6">
            <div class="conflict-pane">
              <div class="conflict-pane__header">
                <div class="conflict-pane__label">
                  <v-icon x-small left>mdi-account-edit-outline</v-icon>
                  Your version
                </div>
                <div class="conflict-pane__meta">Unsaved — {{ localLineCount }} lines</div>
              </div>
              <pre class="conflict-pane__body"><code
              ><span
                v-for="(line, index) in localLines"
                :key="`mine-${index}`"
                class="conflict-line"
                :class="{ 'conflict-line--changed': isMineChanged(index) }"
              >{{ line || ' ' }}
</span></code></pre>
            </div>
          </v-col>

          <v-col cols="12" md="6">
            <div class="conflict-pane">
              <div class="conflict-pane__header">
                <div class="conflict-pane__label">
                  <v-icon x-small left>mdi-cloud-download-outline</v-icon>
                  Their version{{ serverVersionLabel }}
                </div>
                <div class="conflict-pane__meta">{{ serverMeta }}</div>
              </div>
              <pre class="conflict-pane__body"><code
              ><span
                v-for="(line, index) in serverLines"
                :key="`theirs-${index}`"
                class="conflict-line"
                :class="{ 'conflict-line--changed': isTheirsChanged(index) }"
              >{{ line || ' ' }}
</span></code></pre>
            </div>
          </v-col>
        </v-row>

        <div class="ds-dialog__hint mt-3">
          Keeping yours replaces their version — their changes will only remain in the draft
          history. Loading theirs discards the edits you have not saved.
        </div>
      </div>

      <div class="ds-dialog__footer">
        <v-btn text small :disabled="saving" @click="$emit('load-theirs')">
          <v-icon left small>mdi-download-outline</v-icon>
          Load theirs (discard my changes)
        </v-btn>
        <v-spacer />
        <v-btn text small :disabled="saving" @click="$emit('input', false)">Cancel</v-btn>
        <v-btn color="primary" small :loading="saving" @click="$emit('keep-mine')">
          <v-icon left small>mdi-content-save-outline</v-icon>
          Keep mine (overwrite)
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
  import { $formatDate } from '@Utils';

  export default {
    name: 'DraftConflictDialog',
    props: {
      value: {
        type: Boolean,
        default: false,
      },
      localMarkdown: {
        type: String,
        default: '',
      },
      serverDraft: {
        type: Object,
        default: null,
      },
      saving: {
        type: Boolean,
        default: false,
      },
    },
    computed: {
      localLines() {
        return (this.localMarkdown || '').split('\n');
      },
      localLineCount() {
        return this.localLines.length;
      },
      serverMarkdown() {
        const draft = this.serverDraft;
        if (!draft) return '';
        // The conflict payload's draft DTO has not been verified end-to-end, so the
        // markdown is probed across the spellings the API plan uses for a draft.
        const candidates = [draft.markdown, draft.Markdown, draft.content, draft.body];
        const found = candidates.find((value) => typeof value === 'string');
        return found || '';
      },
      serverLines() {
        return this.serverMarkdown.split('\n');
      },
      serverVersionLabel() {
        const draft = this.serverDraft;
        const version = draft ? draft.version ?? draft.Version : null;
        return version || version === 0 ? ` (v${version})` : '';
      },
      serverMeta() {
        const draft = this.serverDraft;
        if (!draft) return '';
        const author =
          draft.createdByName || draft.authorName || draft.createdBy || draft.author || '';
        const timestamp = draft.createdAt || draft.createdOn || draft.modifiedAt || draft.updatedAt;
        const when = timestamp ? $formatDate(timestamp, 'DD/MM/YYYY HH:mm') : '';
        return [author, when].filter(Boolean).join(' · ') || 'Latest saved version';
      },
      /**
       * Line indexes that differ between the two panes, as a plain Set — a positional
       * comparison, not a real diff. Deliberately dependency-free: it highlights where the
       * two versions stop agreeing, which is enough to orient the user, and never claims to
       * be a merge view.
       */
      changedLines() {
        const mine = this.localLines;
        const theirs = this.serverLines;
        const max = Math.max(mine.length, theirs.length);
        const changed = new Set();
        for (let index = 0; index < max; index += 1) {
          if (mine[index] !== theirs[index]) changed.add(index);
        }
        return changed;
      },
    },
    methods: {
      isMineChanged(index) {
        return this.changedLines.has(index);
      },
      isTheirsChanged(index) {
        return this.changedLines.has(index);
      },
    },
  };
</script>

<style scoped lang="scss">
  .ds-dialog__body {
    max-height: 60vh;
  }

  .conflict-pane {
    display: flex;
    flex-direction: column;
    border: 1px solid var(--ds-border-subtle, rgba(255, 255, 255, 0.12));
    border-radius: var(--ds-radius-md, 8px);
    overflow: hidden;
    height: 100%;
  }

  .conflict-pane__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--ds-space-sm, 8px);
    padding: var(--ds-space-sm, 8px) var(--ds-space-md, 16px);
    border-bottom: 1px solid var(--ds-border-subtle, rgba(255, 255, 255, 0.12));
    background: rgba(255, 255, 255, 0.04);
  }

  .conflict-pane__label {
    font-size: 0.8125rem;
    font-weight: 600;
    display: flex;
    align-items: center;
  }

  .conflict-pane__meta {
    font-size: 0.75rem;
    opacity: 0.6;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .conflict-pane__body {
    margin: 0;
    padding: var(--ds-space-sm, 8px) var(--ds-space-md, 16px);
    font-family: var(--ds-font-family-mono, monospace);
    font-size: 0.75rem;
    line-height: 1.6;
    white-space: pre-wrap;
    word-break: break-word;
    overflow-y: auto;
    max-height: 40vh;
    background: transparent;
  }

  .conflict-line {
    display: block;
  }

  .conflict-line--changed {
    background: rgba(255, 193, 7, 0.16);
    border-radius: 2px;
  }

  .theme--light {
    .conflict-pane__header {
      background: rgba(0, 0, 0, 0.03);
    }

    .conflict-line--changed {
      background: rgba(255, 193, 7, 0.28);
    }
  }
</style>
